import { IS_COSTUMES_RENTALS } from './constants';
import { createImageVariantConfig } from './sdkLoader';

export const isRentalSearchPage = () =>
  typeof window !== 'undefined' && window?.location?.pathname === IS_COSTUMES_RENTALS;
export const searchListingConfig = (variantPrefix, aspectRatio) => {
  // Helper function for creating image variant configurations
  return {
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'metadata',
      'deleted',
      'state',
      'publicData',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      // These help rendering of 'purchase' listings,
      // when transitioning from search page to listing page
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile'],
    'fields.image': [
      'variants.custom',
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig('custom', 640, 680 / 640),
    'limit.images': 1,
  };
};

export const resultInfiniteScrollIds = (newData, oldData) => {
  const mergedIds = new Set([
    ...oldData.map(item => item.uuid), // Use `uuid` for uniqueness
    ...newData.data.map(item => item.id.uuid), // Ensure `id.uuid` structure is consistent
  ]);
  return Array.from(mergedIds).map(uuid => ({ uuid })); // Convert back to desired structure
};

// Function to add or update the resultFields array without duplicating enumOptions
export const addOrUpdateField = (resultFields, field) => {
  // Extract attribute name from field key
  let attributeName = field.key.includes('_') ? field.key.split('_')[1] : field.key;
  // Standardize attribute name
  switch (attributeName) {
    case 'genreOption':
      attributeName = 'genre';
      break;
    default:
      break;
  }

  // Capitalize the attribute name for labels
  const capitalizedAttribute = attributeName.charAt(0).toUpperCase() + attributeName.slice(1);

  // Check if field already exists in resultFields
  let existingEntry = resultFields.find(entry => entry.key === attributeName);

  if (existingEntry) {
    // Track unique enum options by 'option' key using a Map
    const uniqueOptionsMap = new Map(existingEntry.enumOptions.map(opt => [opt.option, opt]));
    field.enumOptions.forEach(opt => {
      if (!uniqueOptionsMap.has(opt.option)) {
        uniqueOptionsMap.set(opt.option, opt);
      }
    });
    existingEntry.enumOptions = Array.from(uniqueOptionsMap.values());
  } else {
    // Add new entry to resultFields if it doesn't exist
    resultFields.push({
      key: attributeName,
      scope: 'public',
      schemaType: 'enum',
      enumOptions: [...field.enumOptions],
      filterConfig: {
        label: capitalizedAttribute,
      },
      showConfig: {
        label: capitalizedAttribute,
      },
    });
  }
};

export const getChildAndAdultsOptions = options => {
  const adultOptions =
    options.filter(
      option =>
        (option.option && option.option.toLowerCase().startsWith('adult')) ||
        (option.label && option.label.toLowerCase().startsWith('adult'))
    ) || [];

  const childOptions =
    options.filter(
      option =>
        (option.option && option.option.toLowerCase().startsWith('child')) ||
        (option.label && option.label.toLowerCase().startsWith('child'))
    ) || [];

  return { adultOptions, childOptions };
};

const normalizeSizeFilterToken = value => {
  if (value == null) {
    return null;
  }

  const normalized = `${value}`
    .trim()
    .toLowerCase()
    .replace(/[()]/g, '')
    .replace(/,/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/_+/g, '-')
    .replace(/-+/g, '-');

  return normalized || null;
};

const SIZE_FILTER_GROUP_ALIASES = [
  ['adult-xxs', ['adult-xxs']],
  ['adult-x-small', ['x-small', 'adult-x-small', 'adult-xs']],
  ['adult-small', ['small', 'adult-small', 'adult-s']],
  ['adult-medium', ['medium', 'adult-medium', 'adult-m']],
  ['adult-large', ['large', 'adult-large', 'adult-l']],
  ['adult-x-large', ['x-large', 'adult-x-large', 'adult-xl']],
  ['adult-2xl', ['2xl', 'adult-xxl', 'adult-2xl']],
  ['adult-3xl', ['3xl', 'adult-3xl']],
];

const normalizeSizeFilterOption = option => {
  if (!option || typeof option !== 'object') {
    return null;
  }

  const rawOption =
    option.option ?? option.key ?? option.values ?? option.value ?? option.id ?? option.label ?? null;
  const rawLabel = option.label ?? option.name ?? rawOption;
  const normalizedOption = rawOption != null ? `${rawOption}`.trim() : '';
  const normalizedLabel = rawLabel != null ? `${rawLabel}`.trim() : '';

  return normalizedOption && normalizedLabel
    ? { ...option, option: normalizedOption, label: normalizedLabel }
    : null;
};

const resolveSizeFilterGroupKey = option => {
  const normalizedOption = normalizeSizeFilterToken(option?.option);
  const normalizedLabel = normalizeSizeFilterToken(option?.label);
  const candidateTokens = [normalizedOption, normalizedLabel].filter(Boolean);

  for (const [groupKey, aliases] of SIZE_FILTER_GROUP_ALIASES) {
    if (candidateTokens.some(token => aliases.includes(token))) {
      return groupKey;
    }
  }

  return normalizedOption || normalizedLabel || null;
};

const uniqueStrings = values => {
  const seen = new Set();
  return (values || []).filter(value => {
    const normalized = value != null ? `${value}`.trim() : '';
    if (!normalized || seen.has(normalized)) {
      return false;
    }

    seen.add(normalized);
    return true;
  });
};

export const buildGroupedSizeFilterOptions = options => {
  const groups = [];
  const groupsByKey = new Map();
  const aliasToPrimary = new Map();
  const primaryToAliases = new Map();

  (options || []).forEach(rawOption => {
    const option = normalizeSizeFilterOption(rawOption);
    if (!option?.option) {
      return;
    }

    const groupKey = resolveSizeFilterGroupKey(option) || option.option;
    const existingGroup = groupsByKey.get(groupKey);

    if (!existingGroup) {
      const nextGroup = { ...option, aliases: [option.option] };
      groupsByKey.set(groupKey, nextGroup);
      groups.push(nextGroup);
      aliasToPrimary.set(option.option, option.option);
      primaryToAliases.set(option.option, nextGroup.aliases);
      return;
    }

    existingGroup.aliases = uniqueStrings([...existingGroup.aliases, option.option]);
    existingGroup.aliases.forEach(alias => aliasToPrimary.set(alias, existingGroup.option));
    primaryToAliases.set(existingGroup.option, existingGroup.aliases);
  });

  return {
    options: groups.map(group => ({
      ...group,
      aliases: [...group.aliases],
    })),
    aliasToPrimary,
    primaryToAliases,
  };
};

export const mapSelectedSizeFilterValues = (selectedValues, options) => {
  const { aliasToPrimary } = buildGroupedSizeFilterOptions(options);

  return uniqueStrings(
    (selectedValues || []).map(value => {
      const normalizedValue = value != null ? `${value}`.trim() : '';
      return aliasToPrimary.get(normalizedValue) || normalizedValue;
    })
  );
};

export const expandGroupedSizeFilterValues = (selectedValues, options) => {
  const { aliasToPrimary, primaryToAliases } = buildGroupedSizeFilterOptions(options);

  return uniqueStrings(
    (selectedValues || []).flatMap(value => {
      const normalizedValue = value != null ? `${value}`.trim() : '';
      const primaryValue = aliasToPrimary.get(normalizedValue) || normalizedValue;
      return primaryToAliases.get(primaryValue) || (primaryValue ? [primaryValue] : []);
    })
  );
};
